import { componentScope, h } from "harmaja"
import * as L from "lonna"
import { Board, Item, isTextItem } from "../../../common/src/domain"
import { Rect } from "../../../common/src/geometry"
import { BoardFocus } from "./board-focus"
import { controlKey, installKeyboardShortcut } from "./keyboard-shortcuts"
import { emptySet } from "../../../common/src/sets"

const plainText = (html: string) => new DOMParser().parseFromString(html, "text/html").body.textContent || ""

// Cmd-F: find items by text. Enter / Shift-Enter cycle matches (select + zoom to), Esc closes.
export const SearchView = ({
    board,
    focus,
    zoomToFit,
}: {
    board: L.Property<Board>
    focus: L.Atom<BoardFocus>
    zoomToFit: (rects: Rect[]) => void
}) => {
    const open = L.atom(false)
    const query = L.atom("")
    const current = L.atom<number | null>(null) // index of the last match navigated to
    const input = L.atom<HTMLInputElement | null>(null)

    const matches: L.Property<Item[]> = L.view(board, query, (b, q) => {
        const needle = q.trim().toLowerCase()
        if (!needle) return []
        return Object.values(b.items).filter(
            (i) => isTextItem(i) && !i.hidden && plainText(i.text).toLowerCase().includes(needle),
        )
    })
    query.pipe(L.changes).forEach(() => current.set(null))

    function goTo(i: number) {
        const ms = matches.get()
        if (!ms.length) return
        const idx = ((i % ms.length) + ms.length) % ms.length
        current.set(idx)
        const item = ms[idx]
        focus.set({ status: "selected", itemIds: new Set([item.id]), connectionIds: emptySet() })
        zoomToFit([item])
    }
    const close = () => {
        open.set(false)
        input.get()?.blur()
    }

    installKeyboardShortcut(controlKey("f"), () => {
        open.set(true)
        setTimeout(() => {
            input.get()?.focus()
            input.get()?.select()
        }, 0)
    })

    const onKeyDown = (e: JSX.KeyboardEvent) => {
        e.stopPropagation()
        const c = current.get()
        if (e.key === "Enter" && e.shiftKey) goTo(c === null ? -1 : c - 1)
        else if (e.key === "Enter") goTo(c === null ? 0 : c + 1)
        else if (e.key === "Escape") close()
    }
    const status = L.view(matches, current, query, (ms, c, q) =>
        !q.trim() ? "" : c === null ? `${ms.length} found` : `${c + 1} / ${ms.length}`,
    )

    return L.view(open, (o) =>
        o ? (
            <div className="board-search" onClick={(e) => e.stopPropagation()}>
                <input
                    ref={input.set}
                    type="search"
                    placeholder="Find on board"
                    value={query}
                    onInput={(e) => query.set(e.currentTarget.value)}
                    onKeyDown={onKeyDown}
                    onKeyUp={(e) => e.stopPropagation()}
                    onKeyPress={(e) => e.stopPropagation()}
                />
                <span className="status">{status}</span>
                <a className="close" onClick={close}>
                    ×
                </a>
            </div>
        ) : null,
    )
}
